import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Navigation from './components/layout/Navigation';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/layout/ScrollToTop';
import Landing from './pages/Landing';
import LandingPreview from './pages/LandingPreview';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import BeatUpload from './components/beats/BeatUpload';
import UserProfile from './components/profile/UserProfile';
import UserSearch from './components/search/UserSearch';
import PrivateRoute from './components/auth/PrivateRoute';
import BeatDetailsPage from './pages/BeatDetailsPage';
import ActivateAccount from './pages/ActivateAccount';
import ContactUs from './pages/ContactUs';
import Faq from './pages/Faq';
import CypherChallenge from './pages/CypherChallenge';
import Messages from './pages/Messages';
import VerseSubmissions from './pages/VerseSubmissions';
import './App.css';
import { AudioPlayerProvider } from './context/AudioPlayerContext';

const BeatPreviewRedirect: React.FC = () => {
  const { beatId } = useParams<{ beatId: string }>();
  return <Navigate to={beatId ? `/beats/${beatId}` : '/home'} replace />;
};

const ProfilePreviewRedirect: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  return <Navigate to={userId ? `/profile/${userId}` : '/home'} replace />;
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AudioPlayerProvider>
          <ScrollToTop />
          <div className="min-h-screen bg-page-light flex flex-col">
            <Navigation />
            <main className="flex-1 flex flex-col min-h-0">
              <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/landing-preview" element={<LandingPreview />} />
              <Route path="/home" element={
                <PrivateRoute>
                  <Home />
                </PrivateRoute>
              } />
              <Route path="/home-preview" element={<Navigate to="/home" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/activate" element={<ActivateAccount />} />
              <Route 
                path="/upload" 
                element={
                  <PrivateRoute>
                    <BeatUpload />
                  </PrivateRoute>
                } 
              />
              <Route path="/profile/:userId" element={
                <PrivateRoute>
                  <UserProfile />
                </PrivateRoute>
              } />
              <Route path="/search" element={
                <PrivateRoute>
                  <UserSearch />
                </PrivateRoute>
              } />
              <Route path="/cypher" element={
                <PrivateRoute>
                  <CypherChallenge />
                </PrivateRoute>
              } />
              <Route path="/messages" element={
                <PrivateRoute>
                  <Messages />
                </PrivateRoute>
              } />
              <Route path="/messages/:conversationId" element={
                <PrivateRoute>
                  <Messages />
                </PrivateRoute>
              } />
              <Route path="/submissions" element={
                <PrivateRoute>
                  <VerseSubmissions />
                </PrivateRoute>
              } />
              <Route path="/beats/:beatId" element={
                <PrivateRoute>
                  <BeatDetailsPage />
                </PrivateRoute>
              } />
              <Route path="/beats-preview/:beatId" element={
                <PrivateRoute>
                  <BeatPreviewRedirect />
                </PrivateRoute>
              } />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/contact-preview" element={<Navigate to="/contact" replace />} />
              <Route path="/faq" element={<Faq />} />
              <Route
                path="/profile-preview/:userId"
                element={
                  <PrivateRoute>
                    <ProfilePreviewRedirect />
                  </PrivateRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </AudioPlayerProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
